/* eslint-disable no-restricted-globals */
import React, { useEffect, useRef, useState } from "react";
import { BiChevronRight } from "react-icons/bi";
import { FaUser, FaMoneyBillWave, FaCalendarAlt, FaClock, FaHandshake, FaEdit, FaTimes } from "react-icons/fa";
import {
  ButtonComp,
  CardComp,
  ModalCompAdvanceUpdate,
  MoneyInputComp,
  SelectComp2,
  SelectCompWithFormattedLabel,
  TextInputComp,
  FormikDatePicker,
  LoanTypeDetails,
  MemberInfoCard,
} from "../../../../components";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import FilterFind from "../../../../components/FilterFind";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  useGetAllMembersQuery,
} from "../../../../store/selectableSlice";
import {
  useViewMemberQuery,
} from "../../../../store/admin/memberManagement/memberSlice";
import { useEnums } from "../../../../hooks/useEnums";
import {
  useRecordExistingLoanMutation,
  useGetSelectableLoanTypesQuery,
} from "../../../../store/admin/loan";
import NonmemberGuarantorForm from "./NonmemberGuarantorForm";
import toast from "react-hot-toast";

const FIELD_LABELS = {
  loan_type_id: "Loan type",
  amount: "Loan amount",
  duration: "Duration",
  duration_type: "Duration type",
  approved_at: "Approval date",
  disbursed_at: "Disbursement date",
  disbursement_description: "Disbursement note",
};

const FIELD_SCROLL_ORDER = [
  "loan_type_id",
  "amount",
  "duration",
  "duration_type",
  "approved_at",
  "disbursed_at",
];

function formatValidationToast(errors = {}) {
  return Object.entries(errors)
    .slice(0, 4)
    .map(([key, message]) => `${FIELD_LABELS[key] || key}: ${message}`)
    .join(" · ");
}

function scrollToFirstFormError(errors = {}) {
  const keys = [
    ...FIELD_SCROLL_ORDER.filter((key) => errors[key]),
    ...Object.keys(errors),
  ];
  for (const key of keys) {
    const el =
      document.querySelector(`[data-error-field="${key}"]`) ||
      document.querySelector(`[name="${key}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof el.focus === "function") {
        try {
          el.focus({ preventScroll: true });
        } catch {
          el.focus();
        }
      }
      return;
    }
  }
}

export default function RecordExistingLoanForm() {
  const [selectedMember, setSelectedMember] = useState("");
  const [loanTypeDetails, setLoanTYpeDetails] = useState({});
  const [guarantors, setGuarantors] = useState([]);
  const [nonMembersGuarantorDetails, setNonMemberGuarantorDetails] = useState(
    []
  );
  const [guarantorSlotMapping, setGuarantorSlotMapping] = useState({}); // Track which slot each non-member belongs to
  const [modalPage, setModalPage] = React.useState("");
  const { data: membersData = [] } = useGetAllMembersQuery();
  const enums = useEnums();
  const navigate = useNavigate();

  // Fetch detailed member information when a member is selected
  const { data: memberDetails, isLoading: isLoadingMemberDetails } =
    useViewMemberQuery(selectedMember?.value, {
      skip: !selectedMember?.value, // Only fetch when a member is selected
    });

  const { data: loanTypes, isLoading: isLoadingLoanTypes } =
    useGetSelectableLoanTypesQuery();

  const formattedLoanTypes = loanTypes?.data?.filter((item) => item && item.id).map((item) => {
    return {
      label: item.name,
      value: item.id,
      max_guarantor: item.max_guarantor,
      min_guarantor: item.min_guarantor,
      min_loan_amount: item.min_loan_amount,
      max_loan_amount: item.max_loan_amount,
      rate: item.rate,
      interest_calculation_mode: item.interest_calculation_mode,
      loan_fee: item.loan_fee,
      // Include all other loan type fields for the details component
      ...item,
    };
  });

  React.useEffect(() => {
    if (loanTypeDetails?.max_guarantor) {
      const initialGuarantors = Array.from(
        { length: loanTypeDetails?.max_guarantor },
        (_, index) => ({
          label: `Guarantor ${index + 1}`,
          value: "",
        })
      );
      setGuarantors(initialGuarantors);
    }
  }, [loanTypeDetails?.max_guarantor]);

  const handleGuarantorChange = (index, value, setFieldValue) => {
    const updatedGuarantors = [...guarantors];
    updatedGuarantors[index].value = value;
    setGuarantors(updatedGuarantors);
    setFieldValue(`guarantor${index + 1}`, value);
  };

  const { allMembers } = React.useMemo(() => {
    const fetchedMembers = Array.isArray(membersData) ? membersData : [];
    const formattedMembers = fetchedMembers
      .filter((item) => item && item.id)
      .map((item) => {
        const first = item.first_name || "";
        const last = item.last_name || "";
        const name =
          `${first} ${last}`.trim() || item.name || "Unknown";
        return {
          label: name,
          name,
          photo: item.photo,
          userId: item.member_no,
          value: item.id,
        };
      });
    return { allMembers: formattedMembers };
  }, [membersData]);

  const isInformalLoan = (loanTypeDetails?.loan_model ?? "").toLowerCase() === "informal";

  const initialValues = {
    loan_type_id: "",
    duration: "",
    duration_type: "",
    amount: "",
    approved_at: "",
    disbursed_at: "",
    disbursement_description: "",
    ...guarantors?.reduce((acc, guarantor, index) => {
      return { ...acc, [`guarantor${index + 1}`]: guarantor.value };
    }, {}),
  };

  const validationSchema = React.useMemo(
    () =>
      Yup.object({
        loan_type_id: Yup.string().required("Loan type is required"),
        duration: isInformalLoan
          ? Yup.mixed()
          : Yup.number()
              .transform((value, original) =>
                original === "" || original == null ? undefined : Number(original)
              )
              .typeError("Enter a valid duration")
              .required("Duration is required")
              .min(1, "Duration must be at least 1"),
        duration_type: isInformalLoan
          ? Yup.string()
          : Yup.string().required("Duration type is required"),
        amount: Yup.number()
          .transform((value, original) => {
            if (original === "" || original == null) return undefined;
            const cleaned = String(original).replace(/,/g, "").trim();
            return cleaned === "" ? undefined : Number(cleaned);
          })
          .typeError("Enter a valid loan amount")
          .required("Loan amount is required")
          .min(
            loanTypeDetails?.min_loan_amount || 0,
            `Amount must be at least ₦${(
              loanTypeDetails?.min_loan_amount || 0
            ).toLocaleString()}`
          )
          .max(
            loanTypeDetails?.max_loan_amount || Infinity,
            `Amount cannot exceed ₦${(
              loanTypeDetails?.max_loan_amount || 0
            ).toLocaleString()}`
          ),
        approved_at: Yup.string().required("Approval date is required"),
        disbursed_at: Yup.string().required("Disbursement date is required"),
        disbursement_description: Yup.string(),
      }),
    [isInformalLoan, loanTypeDetails?.min_loan_amount, loanTypeDetails?.max_loan_amount]
  );
  const getMemberGuarantors = (values) => {
    return Object.keys(values)
      .filter((key) => key.startsWith("guarantor") && values[key])
      .map((key) => values[key]);
  };

  const filterNonMemberGuarantors = (guarantors) => {
    return guarantors.filter(
      (item) =>
        typeof item !== "string" || !item.includes("non_member_guarantor")
    );
  };

  const [recordExistingLoan, { isLoading: isCreating }] =
    useRecordExistingLoanMutation();

  const handleFormSubmit = async (values, { resetForm }) => {
    if (!selectedMember?.value) {
      return toast.error("Please select a member");
    }
    const member_id = selectedMember.value;
    let { loan_type_id, duration, duration_type, amount } = values;

    // Informal: duration/plan not used; server derives plan from duration_type for formal
    if (isInformalLoan) {
      duration = 0;
      duration_type = "MONTHS";
    }

    // Get all guarantors from form values
    const allGuarantors = getMemberGuarantors(values);
    
    // Filter out non-member guarantor placeholders
    let formattedMemberGuarantors = filterNonMemberGuarantors(allGuarantors);
    
    // Convert to numbers and filter out invalid values
    formattedMemberGuarantors = formattedMemberGuarantors
      .map((id) => {
        // Convert to number if it's a string
        const numId = typeof id === "string" ? parseInt(id, 10) : id;
        // Return only valid numbers
        return !isNaN(numId) && numId > 0 ? numId : null;
      })
      .filter((id) => id !== null); // Remove null values
    
    // Remove duplicates
    formattedMemberGuarantors = [...new Set(formattedMemberGuarantors)];
    
    // Remove the applicant member from guarantors (member can't be their own guarantor)
    formattedMemberGuarantors = formattedMemberGuarantors.filter(
      (guarantorId) => guarantorId !== member_id
    );

    // Count non-member guarantors
    const nonMemberGuarantorsCount = nonMembersGuarantorDetails?.length || 0;
    
    // Total guarantors = member guarantors + non-member guarantors
    const totalGuarantors = formattedMemberGuarantors.length + nonMemberGuarantorsCount;

    // Validate minimum guarantors if required
    const minGuarantors = loanTypeDetails?.min_guarantor || 0;
    if (minGuarantors > 0 && totalGuarantors < minGuarantors) {
      return toast.error(
        `Please select at least ${minGuarantors} guarantor(s)`
      );
    }

    // Validate that we have guarantors if max_guarantor is set
    if (loanTypeDetails?.max_guarantor && loanTypeDetails?.max_guarantor > 0) {
      if (totalGuarantors === 0 && minGuarantors === 0) {
        // If no guarantors selected but max is set, check if it's optional
        // For now, we'll allow empty if min is 0
      } else if (totalGuarantors > loanTypeDetails.max_guarantor) {
        return toast.error(
          `Maximum ${loanTypeDetails.max_guarantor} guarantor(s) allowed`
        );
      }
    }

    let { approved_at, disbursed_at, disbursement_description } = values;

    const payload = {
      member_id,
      loan_type_id,
      duration,
      duration_type,
      amount,
      approved_at: moment(approved_at).format("YYYY-MM-DD"),
      disbursed_at: moment(disbursed_at).format("YYYY-MM-DD"),
      disbursement_description: disbursement_description || undefined,
      member_guarantors: formattedMemberGuarantors,
      non_member_guarantors: (nonMembersGuarantorDetails || []).map((g) => ({
        ...g,
        phone: String(g?.phone ?? "").trim(),
        email: String(g?.email ?? "").trim(),
        name: String(g?.name ?? "").trim(),
      })),
    };

    const response = await recordExistingLoan(payload);
    const { data, error } = response;
    if (error) {
      return toast.error(error?.data?.message || "An error occured");
    } else if (data?.status === false) {
      return toast.error(data?.message || "An error occured");
    } else {
      toast.success(data?.message || "Existing loan recorded");
      const loanId = data?.data?.id;
      resetForm();
      if (loanId) {
        navigate(`/super_admin/loan_management/record_existing_loan/${loanId}`);
      } else {
        navigate("/super_admin/loan_management/record_existing_loan");
      }
    }
  };

  const [currentGuarantorSlot, setCurrentGuarantorSlot] = useState(null);
  const [editingGuarantorIndex, setEditingGuarantorIndex] = useState(null);

  const useModal = [
    {
      name: "NonMemberGuarantor",
      component: (
        <NonmemberGuarantorForm
          Back={() => {
            setModalPage("");
            setCurrentGuarantorSlot(null);
            setEditingGuarantorIndex(null);
          }}
          setNonMemberGuarantorDetails={(callback) => {
            // The form passes a function: (initial) => [...initial, values]
            setNonMemberGuarantorDetails((prev) => {
              const newDetails = typeof callback === 'function' ? callback(prev) : [...prev, callback];
              // Track which slot this guarantor belongs to (only for new guarantors)
              if (currentGuarantorSlot !== null && editingGuarantorIndex === null) {
                setGuarantorSlotMapping((prevMapping) => ({
                  ...prevMapping,
                  [currentGuarantorSlot]: newDetails.length - 1, // Index in nonMembersGuarantorDetails array
                }));
              }
              return newDetails;
            });
            setModalPage("");
            setCurrentGuarantorSlot(null);
            setEditingGuarantorIndex(null);
          }}
          initialData={
            editingGuarantorIndex !== null
              ? nonMembersGuarantorDetails[editingGuarantorIndex]
              : null
          }
          editIndex={editingGuarantorIndex}
        />
      ),
    },
  ];
  return (
    <section className="px-lg-3 min-vh-100 pb-5 mb-3">
      <ModalCompAdvanceUpdate
        pageName={modalPage}
        arrayComp={useModal}
        handleClose={() => setModalPage()}
        centered
        size={"lg"}
      />
      <FilterFind rightSide={false} title="Record existing loan" />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {({ values, errors, touched, setFieldValue, submitCount, isValid }) => (
          <RecordExistingLoanFormBody
            values={values}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            submitCount={submitCount}
            isValid={isValid}
            selectedMember={selectedMember}
            setSelectedMember={setSelectedMember}
            allMembers={allMembers}
            memberDetails={memberDetails}
            isLoadingMemberDetails={isLoadingMemberDetails}
            loanTypeDetails={loanTypeDetails}
            setLoanTYpeDetails={setLoanTYpeDetails}
            formattedLoanTypes={formattedLoanTypes}
            isLoadingLoanTypes={isLoadingLoanTypes}
            isInformalLoan={isInformalLoan}
            enums={enums}
            guarantors={guarantors}
            nonMembersGuarantorDetails={nonMembersGuarantorDetails}
            guarantorSlotMapping={guarantorSlotMapping}
            setGuarantorSlotMapping={setGuarantorSlotMapping}
            setNonMemberGuarantorDetails={setNonMemberGuarantorDetails}
            setCurrentGuarantorSlot={setCurrentGuarantorSlot}
            setEditingGuarantorIndex={setEditingGuarantorIndex}
            setModalPage={setModalPage}
            handleGuarantorChange={handleGuarantorChange}
            isCreating={isCreating}
          />
        )}
      </Formik>
    </section>
  );
}

function RecordExistingLoanFormBody({
  values,
  errors,
  touched,
  setFieldValue,
  submitCount,
  isValid,
  selectedMember,
  setSelectedMember,
  allMembers,
  memberDetails,
  isLoadingMemberDetails,
  loanTypeDetails,
  setLoanTYpeDetails,
  formattedLoanTypes,
  isLoadingLoanTypes,
  isInformalLoan,
  enums,
  guarantors,
  nonMembersGuarantorDetails,
  guarantorSlotMapping,
  setGuarantorSlotMapping,
  setNonMemberGuarantorDetails,
  setCurrentGuarantorSlot,
  setEditingGuarantorIndex,
  setModalPage,
  handleGuarantorChange,
  isCreating,
}) {
  const lastSubmitCount = useRef(0);
  const [memberTouched, setMemberTouched] = useState(false);
  const showMemberError =
    (memberTouched || submitCount > 0) && !selectedMember?.value;

  useEffect(() => {
    if (submitCount <= lastSubmitCount.current) return;
    lastSubmitCount.current = submitCount;

    if (!selectedMember?.value) {
      setMemberTouched(true);
      toast.error("Please select a member before recording this loan");
      document
        .getElementById("record-existing-loan-member")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (!isValid && Object.keys(errors || {}).length > 0) {
      toast.error(
        `Please fix the highlighted fields. ${formatValidationToast(errors)}`
      );
      scrollToFirstFormError(errors);
    }
  }, [submitCount, isValid, errors, selectedMember?.value]);

  return (
    <Form>
            {/* Member Selection Section */}
            <CardComp 
              cardBodyClassName={"row py-0 gx-5 gy-4"}
              cardClassName={"border-0 rounded-10 px-lg-4 mb-4 pt-4 shadow3"}
            >
              <div className="col-12 mb-2">
                <h5 className="fw-bold mb-1 text-black1">
                  Member Information
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                  Select the member who already has this loan
                </p>
              </div>
              
              <div className="col-12" id="record-existing-loan-member" data-error-field="member">
                <label className="d-flex align-items-center mb-2 fw-bold text-black1">
                  <FaUser className="me-2" style={{ color: "var(--color-primary)", fontSize: "16px" }} />
                  Member Name
                  <span className="text-danger ms-1">*</span>
                </label>
                <div className="d-flex gap-2 align-items-end">
                  <div className="flex-grow-1">
                    <SelectCompWithFormattedLabel
                      setOption={(option) => {
                        setSelectedMember(option || "");
                        setMemberTouched(true);
                      }}
                      arrayComp={allMembers}
                      selectedOption={selectedMember}
                      placeholder="Search and select a member"
                    />
                  </div>
                  {selectedMember?.value && (
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm mb-1 d-inline-flex align-items-center justify-content-center"
                      onClick={() => {
                        setSelectedMember("");
                        setMemberTouched(true);
                      }}
                      title="Clear member selection"
                      aria-label="Clear member selection"
                      style={{ minWidth: "40px", height: "38px" }}
                    >
                      <FaTimes size={14} />
                    </button>
                  )}
                </div>
                {showMemberError && (
                  <small className="text-danger d-block mt-1">
                    Please select a member from the list
                  </small>
                )}
              </div>

              {selectedMember && (
                <div className="col-12 mt-2">
                  {isLoadingMemberDetails ? (
                    <div className="d-flex justify-content-center p-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <MemberInfoCard
                      member={{
                        ...selectedMember,
                        ...memberDetails,
                        // credit_score: memberDetails?.credit_score || 0, // paused until credit score is implemented
                        total_loans: memberDetails?.total_loans || 0,
                        active_loans: memberDetails?.active_loans || 0,
                        repayment_rate: memberDetails?.repayment_rate || 0,
                        is_eligible: memberDetails?.is_eligible ?? true,
                        branch_name:
                          memberDetails?.branch?.name || "Main Branch",
                        created_at:
                          memberDetails?.created_at || new Date().toISOString(),
                      }}
                    />
                  )}
                </div>
              )}
            </CardComp>

            {/* Loan Details Section */}
            <CardComp 
              cardBodyClassName={"row py-0 gx-5 gy-4"}
              cardClassName={"border-0 rounded-10 px-lg-4 mb-4 pt-4 shadow3"}
            >
              <div className="col-12 mb-2">
                <h5 className="fw-bold mb-1 text-black1">
                  Loan Details
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                  Same fields as a new loan application (formal or informal). Guarantors are recorded only — they will not need to accept.
                </p>
              </div>

              {/* Loan Type - Full Width */}
              <div className="col-12" data-error-field="loan_type_id">
                <label className="d-flex align-items-center mb-2 fw-bold text-black1">
                  <FaHandshake className="me-2" style={{ color: "var(--color-primary)", fontSize: "16px" }} />
                  Loan Type
                  <span className="text-danger ms-1">*</span>
                </label>
                <SelectComp2
                  placeholder={"Select loan type"}
                  arrayComp={formattedLoanTypes}
                  isLoading={isLoadingLoanTypes}
                  selectedOption={values.loan_type_id}
                  setSelectedOption={(selectedOption) => {
                    if (selectedOption) {
                      setLoanTYpeDetails(selectedOption);
                      setFieldValue("loan_type_id", selectedOption.value);
                    } else {
                      setLoanTYpeDetails({});
                      setFieldValue("loan_type_id", "");
                    }
                  }}
                  errors={
                    (touched.loan_type_id || submitCount > 0) && errors.loan_type_id
                      ? [errors.loan_type_id]
                      : []
                  }
                />
              </div>

              {/* Loan Type Details */}
              {loanTypeDetails?.id && (
                <div className="col-12 mt-2">
                  <LoanTypeDetails loanType={loanTypeDetails} />
                </div>
              )}

              {/* Loan Amount - Full Width */}
              <div className="col-12" data-error-field="amount">
                <label className="d-flex align-items-center mb-2 fw-bold text-black1">
                  <FaMoneyBillWave className="me-2" style={{ color: "var(--color-primary)", fontSize: "16px" }} />
                  Loan Amount
                  <span className="text-danger ms-1">*</span>
                </label>
                <MoneyInputComp
                  placeholder={"Enter loan amount"}
                  descClassName="text-muted"
                  desc={
                    loanTypeDetails?.min_loan_amount &&
                    loanTypeDetails?.max_loan_amount
                      ? `Amount must be between ₦${loanTypeDetails.min_loan_amount.toLocaleString()} - ₦${loanTypeDetails.max_loan_amount.toLocaleString()}`
                      : "Enter the loan amount"
                  }
                  name={"amount"}
                />
              </div>

              {/* Duration, Duration Type, Repayment Plan - not used for informal (pay at will) */}
              {!isInformalLoan && (
                <>
                  <div className="col-12 mb-3 mt-2">
                    <h6 className="fw-bold mb-1 text-black1">
                      Loan Duration
                    </h6>
                    <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                      Set the loan repayment period
                    </p>
                  </div>

                  <div className="col-12 col-lg-6" data-error-field="duration">
                    <label className="d-flex align-items-center mb-2 fw-bold text-black1">
                      <FaClock className="me-2" style={{ color: "var(--color-primary)", fontSize: "16px" }} />
                      Duration
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <TextInputComp
                      placeholder={"Enter duration (e.g., 12)"}
                      name="duration"
                      type="number"
                    />
                  </div>

                  <div className="col-12 col-lg-6" data-error-field="duration_type">
                    <label className="d-flex align-items-center mb-2 fw-bold text-black1">
                      <FaCalendarAlt className="me-2" style={{ color: "var(--color-primary)", fontSize: "16px" }} />
                      Duration Type
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <SelectComp2
                      placeholder={"Select duration type"}
                      arrayComp={enums["DurationType"] ?? []}
                      selectedOption={values.duration_type}
                      setSelectedOption={(selectedOption) =>
                        setFieldValue("duration_type", selectedOption?.value || "")
                      }
                      errors={
                        (touched.duration_type || submitCount > 0) &&
                        errors.duration_type
                          ? [errors.duration_type]
                          : []
                      }
                    />
                  </div>

                  <div className="col-12">
                    <p className="text-muted mb-0 small">
                      Repayments follow the duration type (e.g. months → monthly).
                    </p>
                  </div>
                </>
              )}
              {isInformalLoan && (
                <div className="col-12 mt-2">
                  <p className="text-muted mb-0 small">
                    Informal loans have no fixed term or schedule; the member can pay any amount at any time.
                  </p>
                </div>
              )}
            </CardComp>

            {/* Historical dates */}
            <CardComp
              cardBodyClassName={"row py-0 gx-5 gy-4"}
              cardClassName={"border-0 rounded-10 px-lg-4 mb-4 pt-4 shadow3"}
            >
              <div className="col-12 mb-2">
                <h5 className="fw-bold mb-1 text-black1">Historical dates</h5>
                <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                  Use the original approval and disbursement dates from your books (past dates).
                </p>
              </div>
              <div className="col-md-6" data-error-field="approved_at">
                <label className="fw-bold text-black1 mb-2">
                  Approval date <span className="text-danger">*</span>
                </label>
                <FormikDatePicker
                  fieldName="approved_at"
                  placeholder="Select approval date"
                />
              </div>
              <div className="col-md-6" data-error-field="disbursed_at">
                <label className="fw-bold text-black1 mb-2">
                  Disbursement date <span className="text-danger">*</span>
                </label>
                <FormikDatePicker
                  fieldName="disbursed_at"
                  placeholder="Select disbursement date"
                />
              </div>
              <div className="col-12">
                <label className="fw-bold text-black1 mb-2">Disbursement note (optional)</label>
                <TextInputComp
                  name="disbursement_description"
                  placeholder="e.g. Migrated from previous ledger"
                />
              </div>
            </CardComp>

            {/* Guarantors Section */}
            {loanTypeDetails?.max_guarantor && guarantors.length > 0 && (
              <CardComp 
                cardBodyClassName={"row py-0 gx-5 gy-4"}
                cardClassName={"border-0 rounded-10 px-lg-4 mb-4 pt-4 shadow3"}
              >
                <div className="col-12 mb-2">
                  <h5 className="fw-bold mb-1 text-black1 d-flex align-items-center">
                    <FaHandshake className="me-2" style={{ color: "var(--color-primary)", fontSize: "18px" }} />
                    Guarantors
                  </h5>
                  <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                    Select {loanTypeDetails?.min_guarantor || 0} - {loanTypeDetails?.max_guarantor || 0} guarantor(s) for this loan.
                    They are recorded for history only and will not need to accept.
                    {loanTypeDetails?.min_guarantor > 0 && (
                      <span className="text-danger ms-1">
                        (Minimum {loanTypeDetails.min_guarantor} required)
                      </span>
                    )}
                  </p>
                </div>
                {guarantors?.map((guarantor, index) => {
                  // Get currently selected guarantors (excluding current field)
                  const selectedGuarantorIds = guarantors
                    .map((g, i) => (i !== index && g.value ? g.value : null))
                    .filter((id) => id && typeof id !== "string" || !id?.includes("non_member_guarantor"));
                  
                  // Get selected member ID if exists
                  const selectedMemberId = selectedMember?.value || null;
                  
                  // Filter out already selected guarantors and the applicant member
                  const availableGuarantors = allMembers.filter((member) => {
                    const memberId = member.value;
                    // Don't show if already selected as another guarantor
                    if (selectedGuarantorIds.includes(memberId)) {
                      return false;
                    }
                    // Don't show if it's the applicant member
                    if (selectedMemberId && memberId === selectedMemberId) {
                      return false;
                    }
                    return true;
                  });

                  // Check if this slot has a non-member guarantor
                  const nonMemberIndex = guarantorSlotMapping[index];
                  const nonMemberGuarantor = nonMemberIndex !== undefined 
                    ? nonMembersGuarantorDetails[nonMemberIndex] 
                    : null;

                  return (
                    <div className="col-12 col-lg-6" key={index}>
                      <label className="d-flex align-items-center mb-2 fw-bold text-black1">
                        <FaUser className="me-2" style={{ color: "var(--color-primary)", fontSize: "16px" }} />
                        {guarantor.label}
                        {index < (loanTypeDetails?.min_guarantor || 0) && (
                          <span className="text-danger ms-1">*</span>
                        )}
                      </label>
                      <SelectComp2
                        placeholder={`Select guarantor ${index + 1}`}
                        arrayComp={[
                          ...availableGuarantors,
                          {
                            value: `non_member_guarantor_${index + 1}`,
                            label: (
                              <ButtonComp
                                btnClassName={
                                  "w-100 bg-primary1 p-2 text-white border-0 rounded"
                                }
                                btnText={
                                  <div
                                    className="d-flex justify-content-between align-items-center"
                                    onClick={() => {
                                      scroll(0, 0);
                                      setCurrentGuarantorSlot(index);
                                      setModalPage("NonMemberGuarantor");
                                    }}
                                  >
                                    Non - member
                                    <BiChevronRight size={20} />
                                  </div>
                                }
                              />
                            ),
                          },
                        ]}
                        selectedOption={
                          nonMemberGuarantor 
                            ? `non_member_guarantor_${index + 1}` 
                            : values[`guarantor${index + 1}`]
                        }
                        setSelectedOption={(selectedOption) =>
                          handleGuarantorChange(
                            index,
                            selectedOption?.value || "",
                            setFieldValue
                          )
                        }
                        errors={
                          touched[`guarantor${index + 1}`] &&
                          errors[`guarantor${index + 1}`]
                            ? [errors[`guarantor${index + 1}`]]
                            : []
                        }
                      />
                      {/* Display non-member guarantor details if added */}
                      {nonMemberGuarantor && (
                        <div 
                          className="mt-2 p-3 rounded"
                          style={{
                            backgroundColor: "#f7f7f7",
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="fw-bold text-black1 mb-0" style={{ fontSize: "14px" }}>
                              External Guarantor Details
                            </h6>
                            <div className="d-flex gap-2 align-items-center">
                              <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() => {
                                  // Edit this non-member guarantor
                                  setEditingGuarantorIndex(nonMemberIndex);
                                  setCurrentGuarantorSlot(index);
                                  setModalPage("NonMemberGuarantor");
                                }}
                                style={{ 
                                  color: "var(--color-secondary)", 
                                  border: "1px solid var(--color-secondary)",
                                  backgroundColor: "transparent",
                                  padding: "4px 8px",
                                  borderRadius: "5px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px"
                                }}
                                title="Edit this guarantor"
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = "var(--color-secondary-soft)";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = "transparent";
                                }}
                              >
                                <FaEdit size={14} />
                                <span style={{ fontSize: "12px" }}>Edit</span>
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() => {
                                  // Remove this non-member guarantor
                                  const newDetails = nonMembersGuarantorDetails.filter((_, i) => i !== nonMemberIndex);
                                  setNonMemberGuarantorDetails(newDetails);
                                  // Update mapping
                                  const newMapping = { ...guarantorSlotMapping };
                                  delete newMapping[index];
                                  // Reindex remaining mappings
                                  Object.keys(newMapping).forEach((slot) => {
                                    const oldIndex = newMapping[slot];
                                    if (oldIndex > nonMemberIndex) {
                                      newMapping[slot] = oldIndex - 1;
                                    }
                                  });
                                  setGuarantorSlotMapping(newMapping);
                                  // Clear the form field
                                  setFieldValue(`guarantor${index + 1}`, "");
                                }}
                                style={{ 
                                  color: "#ff4343", 
                                  border: "1px solid #ff4343",
                                  backgroundColor: "transparent",
                                  padding: "4px 8px",
                                  borderRadius: "5px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px"
                                }}
                                title="Remove this guarantor"
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = "rgba(255, 67, 67, 0.1)";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = "transparent";
                                }}
                              >
                                <i className="fas fa-times" style={{ fontSize: "14px" }}></i>
                                <span style={{ fontSize: "12px" }}>Remove</span>
                              </button>
                            </div>
                          </div>
                          <div className="d-flex flex-column gap-1" style={{ fontSize: "13px" }}>
                            <div>
                              <span className="text-muted">Name:</span>{" "}
                              <span className="fw-bold text-black1">{nonMemberGuarantor.name || "N/A"}</span>
                            </div>
                            <div>
                              <span className="text-muted">Email:</span>{" "}
                              <span className="fw-bold text-black1">{nonMemberGuarantor.email || "N/A"}</span>
                            </div>
                            <div>
                              <span className="text-muted">Phone:</span>{" "}
                              <span className="fw-bold text-black1">{nonMemberGuarantor.phone || "N/A"}</span>
                            </div>
                            <div>
                              <span className="text-muted">Occupation:</span>{" "}
                              <span className="fw-bold text-black1">{nonMemberGuarantor.occupation || "N/A"}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardComp>
            )}

            {/* Submit Button */}
            <div className="d-flex justify-content-start gap-3 mt-4">
              <ButtonComp
                type="submit"
                btnText={"Record existing loan"}
                containerClassName="w-auto"
                btnClassName={
                  "bg-primary1 py-2 px-5 fw-bold text-white border-0 rounded"
                }
                ButtonInlineStyle={{ minWidth: "200px" }}
                loading={isCreating}
              />
            </div>
    </Form>
  );
}
